/**
 * The signed-in account in the app bar from `sm` up: one pill that names the
 * account and opens a panel holding what the phone's More drawer holds — the
 * role, the full name and the email, the sample views for super admins, the
 * sample-student switch for the teachers offered it, and Log out.
 *
 * The name is the one thing in the bar that may shrink: it gives way with an
 * ellipsis, the buttons beside it never do, and the panel says it in full.
 * While the sample-student view is on, the pill itself says so on the amber
 * wash (DESIGN.md §4), and the way back is in the panel it opens, so the
 * mode and its exit are one control. Modelled on FocusPicker: a Popover with
 * dialog semantics rather than a Menu, whose list would put focus on the
 * account's name instead of an action.
 *
 * Update when the account facts, either sample switch, or the log-out path
 * change. Design: docs/designs/app-header-2026-10-13/design.md.
 */
import { useId, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  alpha,
  Box,
  ButtonBase,
  Divider,
  Popover,
  type Theme,
} from '@mui/material';

import { type AccountState, NOT_ACTIVATED_LABEL } from '@/auth/accountState';
import {
  SAMPLE_VIEW_LABEL,
  useDemoViewSwitch,
} from '@/components/common/DemoViewSwitch';

import { accountRowSx } from './accountPanelStyles';
import SandboxViewSwitch, { useSandboxEntry } from './SandboxViewSwitch';

interface AccountMenuProps {
  /** The account's display name (`accountDisplayName`). */
  name: string;
  email: string | null;
  /** The confirmed role word, or '' while the role is unconfirmed. */
  roleLabel: string;
  accountState: AccountState;
  onLogOut: () => void;
}

/** The amber wash the sample-student view and "Not activated yet" wear. */
const amberWash = (theme: Theme) => ({
  backgroundColor: alpha(theme.palette.warning.dark, 0.16),
  borderColor: alpha(theme.palette.warning.dark, 0.55),
});

const AccountMenu = ({
  name,
  email,
  roleLabel,
  accountState,
  onLogOut,
}: AccountMenuProps) => {
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const sampleView = useDemoViewSwitch();
  const sandboxEntry = useSandboxEntry();
  const inSampleView = sampleView.active;
  const pillText = inSampleView ? SAMPLE_VIEW_LABEL : name;

  const close = () => setOpen(false);

  return (
    <>
      <ButtonBase
        ref={pillRef}
        aria-label={`Your account: ${pillText}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(true)}
        sx={theme => ({
          position: 'relative',
          display: 'inline-flex',
          gap: '6px',
          flex: '0 1 auto',
          minWidth: 0,
          maxWidth: { sm: 260, md: 360 },
          height: 40,
          pl: inSampleView ? '12px' : '16px',
          pr: '10px',
          border: '1px solid',
          borderColor: 'var(--border)',
          borderRadius: 'var(--radius-pill)',
          backgroundColor: 'var(--paper)',
          ...(inSampleView ? amberWash(theme) : {}),
          ...(open ? { borderColor: 'var(--cobalt)' } : {}),
          color: 'var(--ink)',
          font: 'inherit',
          fontSize: inSampleView ? 13 : 14,
          fontWeight: inSampleView ? 800 : 700,
          letterSpacing: '-0.01em',
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
          transition:
            'background-color 200ms var(--ease-default, ease-out), border-color 200ms var(--ease-default, ease-out)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          '&:hover': inSampleView ? {} : { backgroundColor: 'var(--bg-alt)' },
          '&:focus-visible': {
            outline: '2px solid var(--cobalt)',
            outlineOffset: 2,
          },
          // 40px to sit level with the buttons beside it; a 48px touch target.
          '&::after': { content: '""', position: 'absolute', inset: '-4px 0' },
        })}
      >
        {inSampleView ? (
          <VisibilityIcon aria-hidden sx={{ flexShrink: 0, fontSize: 18 }} />
        ) : null}
        <Box
          component="span"
          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {pillText}
        </Box>
        <ExpandMoreIcon
          aria-hidden
          sx={{
            flexShrink: 0,
            fontSize: 18,
            color: 'var(--ink-muted)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 200ms var(--ease-default, ease-out)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={pillRef.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            id: panelId,
            role: 'dialog',
            'aria-label': 'Your account',
            sx: {
              mt: '6px',
              width: 288,
              maxWidth: 'calc(100vw - 32px)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-hero)',
            },
          },
        }}
      >
        <Box sx={{ p: '8px' }}>
          <Box sx={{ px: '12px', pt: '8px', pb: '12px' }}>
            {roleLabel && !inSampleView ? (
              <Box
                sx={{
                  mb: '6px',
                  color: 'var(--ink-muted)',
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  lineHeight: 1.3,
                  textTransform: 'uppercase',
                }}
              >
                {roleLabel}
              </Box>
            ) : null}
            <Box
              sx={{
                fontSize: 17,
                fontWeight: 800,
                letterSpacing: '-0.015em',
                lineHeight: 1.25,
                overflowWrap: 'anywhere',
              }}
            >
              {name}
            </Box>
            {email ? (
              <Box
                sx={{
                  mt: '3px',
                  color: 'var(--ink-muted)',
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.45,
                  overflowWrap: 'anywhere',
                }}
              >
                {email}
              </Box>
            ) : null}
            {accountState === 'inactive' ? (
              <Box
                component="span"
                sx={theme => ({
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 26,
                  mt: '10px',
                  px: '10px',
                  border: '1px solid',
                  borderRadius: 'var(--radius-pill)',
                  ...amberWash(theme),
                  color: 'var(--ink)',
                  fontSize: 12,
                  fontWeight: 800,
                })}
              >
                {NOT_ACTIVATED_LABEL}
              </Box>
            ) : null}
          </Box>
          {sandboxEntry ? (
            <>
              <Divider sx={{ my: '4px', mx: '-8px' }} />
              <SandboxViewSwitch />
            </>
          ) : null}
          {sampleView.available ? (
            <>
              <Divider sx={{ my: '4px', mx: '-8px' }} />
              <ButtonBase
                aria-disabled={sampleView.isSwitching || undefined}
                onClick={() => {
                  // The page reloads once the switch lands; until then the
                  // row stays focused and says so.
                  if (!sampleView.isSwitching) sampleView.toggle();
                }}
                sx={accountRowSx}
              >
                {inSampleView ? (
                  <VisibilityOffIcon aria-hidden />
                ) : (
                  <VisibilityIcon aria-hidden />
                )}
                {sampleView.label}
              </ButtonBase>
              {sampleView.errorMessage ? (
                <Box
                  role="alert"
                  sx={{
                    px: '12px',
                    pb: '8px',
                    color: 'error.main',
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 1.45,
                  }}
                >
                  {sampleView.errorMessage}
                </Box>
              ) : null}
            </>
          ) : null}
          <Divider sx={{ my: '4px', mx: '-8px' }} />
          <ButtonBase
            onClick={() => {
              close();
              onLogOut();
            }}
            sx={accountRowSx}
          >
            <LogoutIcon aria-hidden />
            Log out
          </ButtonBase>
        </Box>
      </Popover>
    </>
  );
};

export default AccountMenu;
